import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash, Link as LinkIcon, CheckCircle, Eye } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Link, useSearchParams } from "react-router-dom";
import { isAdmin } from "@/lib/roles";

const KINDS = ["Enrollment form", "Contract", "Document request", "Health form", "Custom form"];
const TYPES = [
  ["short_text", "Short text"], ["long_text", "Paragraph"], ["number", "Number"], ["date", "Date"],
  ["yes_no", "Yes / No"], ["select", "Choose one"], ["checkbox", "Agreement checkbox"],
];

const blank = { title: "", kind: "Enrollment form", description: "", fields: [],
                require_signature: true, active: true, due_date: "" };

// Questions can be added and removed mid-list, so each row carries its own identity rather than
// leaning on the array index (which would move typed text onto a different question after a delete).
let keySeq = 0;
const nextKey = () => { keySeq += 1; return keySeq; };
const withKeys = (fields) => (fields || []).map((x) => (x.key ? x : { ...x, key: `q${nextKey()}` }));

export default function Forms() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [forms, setForms] = useState([]);
  const [subs, setSubs] = useState([]);
  const [open, setOpen] = useState(params.get("action") === "add" && isAdmin(user));
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(blank);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/forms"), api.get("/form-submissions")]);
    setForms(a.data);
    setSubs(b.data);
  };
  useEffect(() => { load(); }, []);

  const addField = () => setF({ ...f, fields: [...f.fields,
    { key: `q${nextKey()}`, label: "", type: "short_text", required: false, options: [] }] });
  const setField = (i, k, v) => {
    const fields = [...f.fields];
    fields[i] = { ...fields[i], [k]: k === "options" ? v.split(",").map((s) => s.trim()).filter(Boolean) : v };
    setF({ ...f, fields });
  };
  const rmField = (i) => setF({ ...f, fields: f.fields.filter((_, j) => j !== i) });

  const save = async () => {
    if (!f.title.trim()) return toast.error("Give the form a title");
    if (f.fields.some((x) => !x.label.trim())) return toast.error("Every question needs a label");
    try {
      const payload = { ...f, fields: f.fields.map(({ key, ...rest }) => rest) };
      delete payload.id; delete payload.created_at; delete payload.created_by;
      delete payload.submission_count;
      if (editing) await api.put(`/forms/${editing}`, payload);
      else await api.post("/forms", payload);
      toast.success(editing ? "Form updated" : "Form created");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/forms/${id}`); toast.success("Form deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const copyLink = (id) => {
    const url = `${window.location.origin}/forms/${id}`;
    navigator.clipboard?.writeText(url);
    toast.success("Shareable link copied — send it to families or embed it on your site");
  };

  const review = async (id) => {
    try { await api.patch(`/form-submissions/${id}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="forms-page">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Paperwork</p>
          <h1 className="mt-3 text-4xl font-bold tracking-tight">Forms & documents</h1>
        </div>
        <Button onClick={() => { setEditing(null); setF(blank); setOpen(true); }}
                className="rounded-full h-12 px-7 bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                data-testid="add-form-btn"><Plus size={18} className="mr-2" /> Build a form</Button>
      </div>

      <Tabs defaultValue="forms" className="mt-8">
        <TabsList className="rounded-full">
          <TabsTrigger value="forms" className="rounded-full font-bold" data-testid="tab-forms">My forms</TabsTrigger>
          <TabsTrigger value="subs" className="rounded-full font-bold" data-testid="tab-submissions">
            Submissions ({subs.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="forms" className="mt-6 grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="forms-list">
          {forms.length === 0 && <p className="text-muted-foreground">No forms yet — build your first one.</p>}
          {forms.map((x) => (
            <div key={x.id} className="bg-white rounded-2xl border border-border p-6" data-testid={`form-card-${x.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0">
                  <p className="font-display text-lg font-semibold truncate">{x.title}</p>
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#3B82F6] mt-1">{x.kind}</p>
                </div>
                <span className={`text-xs font-bold px-3 py-1 rounded-full shrink-0 ${
                  x.active ? "bg-emerald-50 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
                  {x.active ? "open" : "closed"}
                </span>
              </div>
              {x.description && <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{x.description}</p>}
              <p className="mt-4 text-sm font-semibold">
                {(x.fields || []).length} question{(x.fields || []).length === 1 ? "" : "s"} ·{" "}
                {x.submission_count} submitted{x.require_signature ? " · signature required" : ""} · v{x.version || 1}
              </p>
              {x.due_date && <p className="mt-1 text-xs font-bold text-[#EF4444]">Due {x.due_date}</p>}
              <div className="mt-5 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => copyLink(x.id)}
                        className="rounded-full font-bold" data-testid={`copy-link-${x.id}`}>
                  <LinkIcon size={15} className="mr-1" /> Share link
                </Button>
                <Button size="sm" variant="outline"
                        onClick={() => { setEditing(x.id); setF({ ...blank, ...x, fields: withKeys(x.fields) }); setOpen(true); }}
                        className="rounded-full font-bold" data-testid={`edit-form-${x.id}`}>Edit</Button>
                {isAdmin(user) && (
                  <Button size="sm" variant="outline" onClick={() => del(x.id)}
                          className="rounded-full font-bold text-[#EF4444]" data-testid={`delete-form-${x.id}`}>
                    <Trash size={15} />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="subs" className="mt-6 space-y-3" data-testid="submissions-list">
          {subs.length === 0 && <p className="text-muted-foreground">No submissions yet.</p>}
          {subs.map((s) => (
            <div key={s.id} className="bg-white rounded-2xl border border-border p-5 flex flex-wrap gap-4 items-center"
                 data-testid={`submission-row-${s.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-bold">{s.student_name} · {s.form_title}</p>
                <p className="text-xs text-muted-foreground mt-1 font-semibold">
                  {s.parent_name || s.parent_email} · {s.submitted_at?.slice(0, 10)}
                  {s.signature ? ` · signed "${s.signature}"` : ""}
                </p>
              </div>
              <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${
                s.status === "Reviewed" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}
                    data-testid={`submission-status-${s.id}`}>{s.status}</span>
              <Button size="sm" variant="outline" onClick={() => setViewing(s)}
                      className="rounded-full font-bold" data-testid={`view-submission-${s.id}`}>
                <Eye size={15} className="mr-1" /> View
              </Button>
              <Button size="sm" onClick={() => review(s.id)}
                      className="rounded-full bg-[#10B981] hover:bg-emerald-600 font-bold"
                      data-testid={`review-submission-${s.id}`}>
                <CheckCircle size={15} className="mr-1" /> {s.status === "Reviewed" ? "Undo" : "Mark reviewed"}
              </Button>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      {/* Builder */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="form-builder-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit form" : "Build a form"}</DialogTitle>
            <DialogDescription className="sr-only">Create custom questions for families to answer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-bold">Title</Label>
              <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
                     placeholder="2026 Enrollment Packet" className="mt-2 h-11 rounded-xl" data-testid="form-builder-title" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold">Type</Label>
                <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                  <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="form-builder-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-bold">Due date (optional)</Label>
                <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })}
                       className="mt-2 h-11 rounded-xl" data-testid="form-builder-due" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-bold">Instructions for families</Label>
              <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
                        className="mt-2 rounded-xl" data-testid="form-builder-description" />
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-sm font-bold">Questions</Label>
                <Button size="sm" variant="outline" onClick={addField} className="rounded-full font-bold"
                        data-testid="add-field-btn"><Plus size={15} className="mr-1" /> Add question</Button>
              </div>
              {f.fields.length === 0 && (
                <p className="text-sm text-muted-foreground">No questions yet — add as many as you need.</p>
              )}
              {f.fields.map((x, i) => (
                <div key={x.key} className="rounded-2xl border border-border p-4 space-y-3" data-testid={`field-row-${i}`}>
                  <div className="flex gap-3">
                    <Input value={x.label} onChange={(e) => setField(i, "label", e.target.value)}
                           placeholder="Question label" className="h-10 rounded-xl" data-testid={`field-label-${i}`} />
                    <Button size="sm" variant="outline" onClick={() => rmField(i)}
                            className="rounded-full text-[#EF4444] shrink-0" data-testid={`remove-field-${i}`}>
                      <Trash size={15} />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-3 items-center">
                    <div className="w-44">
                      <Select value={x.type} onValueChange={(v) => setField(i, "type", v)}>
                        <SelectTrigger className="h-10 rounded-xl" data-testid={`field-type-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <label className="flex items-center gap-2 text-sm font-semibold">
                      <input type="checkbox" checked={x.required}
                             onChange={(e) => setField(i, "required", e.target.checked)}
                             data-testid={`field-required-${i}`} /> Required
                    </label>
                    {x.type === "select" && (
                      <Input value={(x.options || []).join(", ")} onChange={(e) => setField(i, "options", e.target.value)}
                             placeholder="Option A, Option B" className="h-10 rounded-xl flex-1 min-w-[12rem]"
                             data-testid={`field-options-${i}`} />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-3 text-sm font-semibold">
                <input type="checkbox" checked={f.require_signature}
                       onChange={(e) => setF({ ...f, require_signature: e.target.checked })}
                       data-testid="form-builder-signature" /> Require a signature
              </label>
              <label className="flex items-center gap-3 text-sm font-semibold">
                <input type="checkbox" checked={f.active}
                       onChange={(e) => setF({ ...f, active: e.target.checked })}
                       data-testid="form-builder-active" /> Accepting responses
              </label>
              {editing && (
                <label className="flex items-center gap-3 text-sm font-semibold text-amber-800">
                  <input type="checkbox" checked={Boolean(f.requires_resign)}
                         onChange={(e) => setF({ ...f, requires_resign: e.target.checked })}
                         data-testid="form-builder-resign" /> This change needs families to sign again
                </label>
              )}
            </div>
            {editing && <p className="text-xs text-muted-foreground">Currently version {f.version || 1}. Changing the title, questions or signature setting creates a new version; every copy already signed stays exactly as it was.</p>}

            <Button onClick={save} className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid="form-builder-save">{editing ? "Save form" : "Create form"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Submission viewer */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="rounded-3xl max-h-[85vh] overflow-y-auto" data-testid="submission-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{viewing?.form_title}</DialogTitle>
            <DialogDescription>
              {viewing?.student_name} · submitted {viewing?.submitted_at?.slice(0, 10)}
            </DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3 text-sm">
              {[["Child", viewing.student_name], ["Parent", viewing.parent_name || "—"],
                ["Email", viewing.parent_email],
                ["Type", viewing.electronic ? `Electronic signature · form v${viewing.form_version}` : viewing.paper ? `Paper copy recorded by ${viewing.recorded_by}` : "Public link submission"]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-border pb-2">
                  <span className="text-muted-foreground font-semibold">{k}</span>
                  <span className="font-semibold text-right">{v}</span>
                </div>
              ))}
              {viewing.signature_id && (
                <Link to={`/admin/signed/${viewing.signature_id}`} className="inline-block text-[#3B82F6] font-bold" data-testid="open-signed-record">Open signed record →</Link>
              )}
              {Object.entries(viewing.answers || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-border pb-2">
                  <span className="text-muted-foreground font-semibold">{k}</span>
                  <span className="font-semibold text-right">{String(v) || "—"}</span>
                </div>
              ))}
              {viewing.signature && (
                <div className="pt-2">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Signed</p>
                  <p className="mt-1 font-display text-2xl">{viewing.signature}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
